<!--
 * @Descripttion: 市场服务合同签署要求配置卡 + 抽屉（向日葵服务助手 · 工业专用）
 *   卡片：标题 + 规则说明 + 摘要统计 + 配置按钮
 *   抽屉：新签约默认策略 + 搜索 + 批量设置 + 逐家开关 + 分页
-->
<template>
  <!-- ======== 入口卡片 ======== -->
  <div class="icr-card">
    <div class="icr-card__top">
      <svg
        viewBox="0 0 24 24"
        class="icr-card__icon"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M9 12l2 2 4-4" />
        <path
          d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.22.45 4.56 1.24"
        />
      </svg>
      <div class="icr-card__header">
        <h2 class="icr-card__title">市场服务合同签署要求</h2>
        <p class="icr-card__desc">
          要求后，服务商在
          <strong>提交结算</strong>
          前必须先上传双方
          <strong>线下签署</strong>
          的合同扫描件，否则无法提交；上传完成后自动解除拦截。
        </p>
      </div>
    </div>

    <div class="icr-card__bottom">
      <span class="icr-card__summary">
        共
        <strong>{{ totalCount }}</strong>
        家服务商，其中
        <strong class="icr-card__summary--hl">{{ requiredCount }}</strong>
        家要求上传
      </span>
      <BaseButton type="primary" @click="openDrawer">
        配置服务商要求
        <svg
          viewBox="0 0 24 24"
          class="icr-card__btn-icon"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </BaseButton>
    </div>
  </div>

  <!-- ======== 配置抽屉 ======== -->
  <Teleport to="body">
    <div
      v-if="drawerVisible"
      class="icr-drawer-mask"
      @click.self="closeDrawer"
    />
    <div v-if="drawerVisible" class="icr-drawer">
      <!-- header -->
      <div class="icr-drawer__header">
        <div>
          <h3 class="icr-drawer__title">配置服务商合同要求</h3>
          <p class="icr-drawer__desc">
            对每家服务商单独设置是否要求上传线下签署合同；未设置默认为不要求。
          </p>
        </div>
        <button class="icr-drawer__close" @click="closeDrawer">×</button>
      </div>

      <!-- 新签约服务商默认策略 -->
      <div class="icr-drawer__strategy">
        <div class="icr-drawer__strategy-main">
          <div class="icr-drawer__strategy-head">
            <svg
              viewBox="0 0 24 24"
              class="icr-drawer__strategy-icon"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 2l7 4v6c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-4z" />
            </svg>
            <span class="icr-drawer__strategy-title">新签约服务商默认策略</span>
          </div>
          <p class="icr-drawer__strategy-desc">
            平台后续为本工业新绑定服务商时，系统按此默认值预置「是否要求上传合同」，免逐家补配、规避人工遗漏。仅作用于新入网服务商，不影响已有配置。
          </p>
        </div>
        <div class="icr-drawer__strategy-toggle">
          <BaseSwitch
            v-model="defaultRequired"
            :active-value="1"
            :inactive-value="0"
            :beforeChange="onToggleDefault"
          />
          <span
            :class="
              defaultRequired
                ? 'icr-drawer__strategy-label--on'
                : 'icr-drawer__strategy-label--off'
            "
          >
            {{ defaultRequired ? '要求' : '不要求' }}
          </span>
        </div>
      </div>

      <!-- 搜索 -->
      <div class="icr-drawer__search">
        <BaseInput
          v-model="searchKeyword"
          placeholder="搜索服务商名称 / 统一社会信用代码"
          @keyup.enter="onSearch"
        />
      </div>

      <!-- 批量操作 -->
      <div class="icr-drawer__batch">
        <span class="icr-drawer__batch-label">批量设置全部服务商</span>
        <div class="icr-drawer__batch-actions">
          <BaseButton type="default" size="small" @click="onBatchSet(true)">
            全部要求上传
          </BaseButton>
          <BaseButton type="default" size="small" @click="onBatchSet(false)">
            全部不要求
          </BaseButton>
        </div>
      </div>

      <!-- 服务商列表 -->
      <div class="icr-drawer__list">
        <BaseTable :columns="tableColumns" :data="pageList" />
      </div>

      <!-- 分页 -->
      <div class="icr-drawer__footer">
        <span>共 {{ filteredTotal }} 家</span>
        <div class="icr-drawer__pager">
          <button
            :disabled="page <= 1"
            class="icr-pager__btn"
            @click="page = page - 1"
          >
            <svg
              viewBox="0 0 24 24"
              class="icr-pager__icon"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span class="icr-pager__text">{{ page }} / {{ pageTotal }}</span>
          <button
            :disabled="page >= pageTotal"
            class="icr-pager__btn"
            @click="page = page + 1"
          >
            <svg
              viewBox="0 0 24 24"
              class="icr-pager__icon"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="tsx" setup>
import { ref, computed, onMounted } from 'vue';
import {
  BaseButton,
  BaseSwitch,
  BaseInput,
  BaseTable,
  useTableColumn,
  XrkMessage
} from 'xrk-components';
import { queryContractReq, switchContractReq } from '../data-source/apis';

interface SpItem {
  finalDeptId: number;
  entpName: string;
  entpCertNo: string;
  isUploadContract: number;
}

const PAGE_SIZE = 8;

const drawerVisible = ref(false);
const searchKeyword = ref('');
const page = ref(1);
const list = ref<SpItem[]>([]);
const defaultRequired = ref(false);
const saving = ref(false);

const filteredList = computed(() => {
  const kw = searchKeyword.value.trim().toLowerCase();
  if (!kw) return list.value;
  return list.value.filter(
    sp =>
      sp.entpName.toLowerCase().includes(kw) ||
      sp.entpCertNo.toLowerCase().includes(kw)
  );
});

const filteredTotal = computed(() => filteredList.value.length);
const pageTotal = computed(() =>
  Math.max(1, Math.ceil(filteredTotal.value / PAGE_SIZE))
);

const pageList = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE;
  return filteredList.value.slice(start, start + PAGE_SIZE);
});

const totalCount = ref(0);
const requiredCount = ref(0);

const tableColumns = [
  useTableColumn({ label: '服务商', prop: 'entpName', minWidth: 160 }),
  useTableColumn({
    label: '统一社会信用代码',
    prop: 'entpCertNo',
    minWidth: 180
  }),
  useTableColumn({
    label: '要求上传合同',
    prop: 'isUploadContract',
    width: 150,
    render: ({ row }) => (
      <div class="icr-toggle-row">
        <BaseSwitch
          v-model={row.isUploadContract}
          active-value={1}
          inactive-value={0}
          beforeChange={() => onToggleSp(row)}
        />
        <span
          class={
            row.isUploadContract
              ? 'icr-toggle-label--on'
              : 'icr-toggle-label--off'
          }
        >
          {row.isUploadContract ? '要求' : '不要求'}
        </span>
      </div>
    )
  })
];

const fetchData = async () => {
  const { data } = await queryContractReq();
  defaultRequired.value = !!data.signMarketContract;
  totalCount.value = data.totalServiceProviderCount || 0;
  requiredCount.value = data.requireUploadCount || 0;
  list.value = data.serviceProviderList || [];
};

onMounted(fetchData);

const refreshCounts = () => {
  requiredCount.value = list.value.filter(
    sp => sp.isUploadContract === 1
  ).length;
};

const openDrawer = () => {
  searchKeyword.value = '';
  page.value = 1;
  drawerVisible.value = true;
};

const closeDrawer = () => {
  drawerVisible.value = false;
};

const onSearch = () => {
  page.value = 1;
};

const onToggleSp = (sp: SpItem) => {
  const next = sp.isUploadContract === 1 ? 0 : 1;
  return new Promise<void>(async (resolve, reject) => {
    if (saving.value) return reject();
    saving.value = true;
    try {
      await switchContractReq({
        isUploadContract: next,
        aspId: sp.finalDeptId
      });
      refreshCounts();
      XrkMessage.success(
        next
          ? `已要求「${sp.entpName}」上传线下合同`
          : `已设为不要求「${sp.entpName}」上传合同`
      );
      resolve();
    } catch {
      reject();
    } finally {
      saving.value = false;
    }
  });
};

const onBatchSet = async (required: boolean) => {
  if (saving.value) return;
  saving.value = true;
  try {
    await switchContractReq({ isUploadContract: required ? 1 : 0 });
    list.value.forEach(sp => {
      sp.isUploadContract = required ? 1 : 0;
    });
    refreshCounts();
    XrkMessage.success(
      required
        ? '已要求全部服务商上传线下合同'
        : '已设为全部服务商不要求上传合同'
    );
  } finally {
    saving.value = false;
  }
};

const onToggleDefault = () => {
  const next = !defaultRequired.value;
  return new Promise<void>(async (resolve, reject) => {
    if (saving.value) return reject();
    saving.value = true;
    try {
      await switchContractReq({ signMarketContract: next ? 1 : 0 });
      XrkMessage.success(
        next
          ? '新签约服务商将默认要求上传合同'
          : '新签约服务商将默认不要求上传合同'
      );
      resolve();
    } catch {
      reject();
    } finally {
      saving.value = false;
    }
  });
};
</script>

<style lang="scss" scoped>
/* ======== 入口卡片 ======== */
.icr-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 16px 20px;
  margin-bottom: 12px;

  &__top {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  &__icon {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    color: #f97316;
    flex: none;
  }
  &__header {
    min-width: 0;
  }
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
  &__desc {
    margin: 4px 0 0;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.6;
    strong {
      color: #4b5563;
    }
  }
  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding-left: 24px;
  }
  &__summary {
    font-size: 14px;
    color: #4b5563;
    strong {
      font-weight: 600;
      color: #1f2937;
    }
    &--hl {
      color: #f97316 !important;
    }
  }
  &__btn-icon {
    width: 14px;
    height: 14px;
    margin-left: 4px;
  }
}

/* ======== 抽屉 ======== */
.icr-drawer-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.4);
}
.icr-drawer {
  position: fixed;
  right: 0;
  top: 0;
  z-index: 2001;
  width: 600px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;

  &__header {
    padding: 16px 20px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
  &__desc {
    margin: 2px 0 0;
    font-size: 12px;
    color: #6b7280;
  }
  &__close {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: #9ca3af;
    font-size: 20px;
    line-height: 1;
    flex: none;
    &:hover {
      background: #f3f4f6;
      color: #4b5563;
    }
  }

  &__strategy {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 20px;
    border-bottom: 1px solid #f3f4f6;
    background: rgba(255, 247, 237, 0.6);
  }
  &__strategy-main {
    min-width: 0;
  }
  &__strategy-head {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__strategy-icon {
    width: 14px;
    height: 14px;
    color: #f97316;
    flex: none;
  }
  &__strategy-title {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
  }
  &__strategy-desc {
    margin: 2px 0 0;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.6;
  }
  &__strategy-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: none;
  }
  &__strategy-label--on {
    font-size: 12px;
    color: #f97316;
    width: 40px;
  }
  &__strategy-label--off {
    font-size: 12px;
    color: #9ca3af;
    width: 40px;
  }

  &__search {
    padding: 12px 20px;
    border-bottom: 1px solid #f3f4f6;
  }
  &__batch {
    padding: 10px 20px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__batch-label {
    font-size: 12px;
    color: #6b7280;
  }
  &__batch-actions {
    display: flex;
    gap: 8px;
  }

  &__list {
    flex: 1;
    overflow-y: auto;
  }
  &__footer {
    padding: 12px 20px;
    border-top: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    color: #4b5563;
  }
  &__pager {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.icr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    text-align: left;
    padding: 10px 20px;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    background: #f9fafb;
    border-bottom: 1px solid #f3f4f6;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  tbody td {
    padding: 14px 20px;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
    vertical-align: middle;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  &__name {
    color: #1f2937;
  }
  &__uscc {
    font-family: 'ui-monospace', 'SFMono-Regular', 'Menlo', 'Monaco', 'Consolas',
      monospace;
    font-size: 12px;
    color: #4b5563;
  }
  &__empty {
    padding: 48px 20px !important;
    text-align: center;
    color: #9ca3af;
  }
}

.icr-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.icr-toggle-label--on {
  font-size: 12px;
  color: #f97316;
}
.icr-toggle-label--off {
  font-size: 12px;
  color: #9ca3af;
}

.icr-pager {
  &__btn {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: #9ca3af;
    &:hover {
      background: #f3f4f6;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
  &__icon {
    width: 14px;
    height: 14px;
  }
  &__text {
    font-size: 12px;
  }
}
</style>
